import { render } from 'preact-render-to-string';
import {
  Line,
  BasicEmailWrapper,
  DoubleLine,
  PrimaryAction,
  Heading,
  SupportFooter,
  SubHeading,
  Mailto,
} from './shared';
import { CourseRow, Tenant, UserProfileRow } from 'server/types';
import { URLS } from 'shared/urls';

/**
 * Welcome email sent to user after course signup.
 */
export function WelcomeToCourse({
  course,
  guide,
  tenant,
}: {
  course: Pick<CourseRow, 'id' | 'title'>;
  guide: Pick<UserProfileRow, 'name' | 'email' | 'displayName'>;
  tenant: Pick<Tenant, 'name' | 'domain' | 'email'>;
}) {
  const courseUrl = URLS.student.course({
    course,
    domain: tenant.domain,
  });
  return render(
    <BasicEmailWrapper footer={<SupportFooter email={tenant.email} />}>
      <Heading>
        Thanks for joining {course.title} with {guide.name || ''}!
      </Heading>
      <SubHeading>How to access your course</SubHeading>
      <Line>You can access your course at any time, here:</Line>
      <PrimaryAction href={courseUrl}>Visit "{course.title}"</PrimaryAction>
      <DoubleLine> </DoubleLine>
      <SubHeading>Need any help?</SubHeading>
      <Line>
        If you have any questions about your course, you can reach your course guide here:{' '}
        <Mailto email={guide.email} />
      </Line>
      <Line>
        For any other help, drop us a line anytime: <Mailto email={tenant.email} />
      </Line>
      <Line>—The {tenant.name} Team</Line>
    </BasicEmailWrapper>,
  );
}

/**
 * Welcome email for free tier.
 */
export function WelcomeToFreeTier({
  tenant,
  ctaUrl,
  shouldResetPassword,
}: {
  tenant: Pick<Tenant, 'name' | 'domain' | 'email'>;
  ctaUrl: string;
  shouldResetPassword: boolean;
}) {
  return render(
    <BasicEmailWrapper footer={<SupportFooter email={tenant.email} />}>
      <Heading>Thanks for joining — we're delighted to have you with us!</Heading>
      <Line>With our free account...</Line>
      <ul>
        <li>You can create unlimited courses and lessons at no charge.</li>
        <li>You can also enroll up to 5 students (free or paid).</li>
      </ul>

      <SubHeading>Next steps</SubHeading>
      <ul style="margin-bottom:8px;">
        <li>
          <a href={`https://${tenant.domain}/account/billing`}>Upgrade your account</a> to start
          signing up more than 5 students.
        </li>
        <li>
          Or contact us for help anytime! Just reply to this email, we'd love to hear from you :)
        </li>
        {shouldResetPassword && (
          <li>
            You can <a href={`https://${tenant.domain}/login`}>log into your account</a> by having a
            link emailed to you whenever you need one (just use the "forgot password" link when
            logging in).
          </li>
        )}
      </ul>
      {shouldResetPassword && (
        <Line>
          You may also find it convenient to set a password for your account so you can log in
          quickly in the future.
        </Line>
      )}
      <PrimaryAction href={ctaUrl}>
        {shouldResetPassword
          ? 'Set a password and access your account'
          : 'Start building your course for free'}
      </PrimaryAction>
    </BasicEmailWrapper>,
  );
}

/**
 * Welcome email for paid tiers.
 */
export function WelcomeToPaidTier({
  tenant,
  ctaUrl,
  shouldResetPassword,
}: {
  tenant?: Pick<Tenant, 'email'>;
  ctaUrl: string;
  shouldResetPassword: boolean;
}) {
  return render(
    <BasicEmailWrapper footer={<SupportFooter email={tenant?.email} />}>
      <Heading>Thanks for joining — we're delighted to have you with us!</Heading>
      {shouldResetPassword && (
        <>
          <Line>
            You may find it convenient to set a password for your account so you can log in quickly
            in the future.
          </Line>
          <Line>P.S. Any questions or need help? Just hit reply, we'd love to hear from you!</Line>
        </>
      )}
      {!shouldResetPassword && (
        <Line>
          Contact us for help anytime! Just reply to this email, we'd love to hear from you :)
        </Line>
      )}
      <PrimaryAction href={ctaUrl}>
        {shouldResetPassword
          ? 'Set a password and access your account'
          : 'Start building your courses'}
      </PrimaryAction>
    </BasicEmailWrapper>,
  );
}
